export const FARMER_RESOURCES = {
  'seller-guide': {
    slug: 'seller-guide',
    path: '/seller-guide',
    title: 'Seller Guide: How to Sell on Taja Bazzar',
    description:
      'Step-by-step guide for farmers in Morang, Sunsari, and Jhapa to list products, manage orders, and grow sales on Taja Bazzar.',
    sections: [
      {
        h: 'Who can sell on Taja Bazzar?',
        p: [
          'Taja Bazzar is open to farmers, growers, and small agricultural producers based in Morang, Sunsari, and Jhapa districts. Whether you operate a family vegetable plot in Biratnagar, a fruit orchard in Jhapa, or a mixed farm in Sunsari, you can create a farmer account and list your harvest directly to local consumers.',
          'You do not need a large commercial operation. Many of our sellers are smallholders who previously relied on middlemen at haat bazaars or wholesale markets. Taja Bazzar gives you a digital storefront so customers can find you by location, product type, and reviews.',
        ],
      },
      {
        h: 'Creating your farmer account',
        p: [
          'Visit the Sign Up page and choose "I\'m a Farmer." You will need a valid email address, phone number, and basic profile information including your farm location. Accurate location data helps consumers find you on the Nearby map and filter by district.',
          'After registration, complete your public profile with a clear photo, a short bio describing your farm and practices, and your specialties (for example: organic vegetables, seasonal fruits, dairy, or rice). Profiles with photos and detailed bios receive significantly more orders than empty profiles.',
        ],
      },
      {
        h: 'Listing your first product',
        p: [
          'From your farmer dashboard, go to Products → Add Product. Each listing should include a descriptive title (e.g. "Fresh Organic Tomatoes — Biratnagar"), accurate price per unit, available quantity, and high-quality photos taken in natural light. Blurry or stock images reduce buyer trust.',
          'Write a honest product description: how the item was grown, whether pesticides were used, expected shelf life, and preferred pickup or delivery options. Mention if produce is harvested the same day — freshness is a major selling point for local buyers in eastern Nepal.',
        ],
        list: [
          'Use clear titles with the product name and key detail (organic, seasonal, etc.)',
          'Upload at least one real photo of your actual produce',
          'Keep stock quantities updated to avoid cancelled orders',
          'Set fair prices by checking similar listings in your district',
          'Respond to customer messages within 24 hours when possible',
        ],
      },
      {
        h: 'Managing orders and payments',
        p: [
          'When a customer places an order, you receive a notification in your dashboard. Confirm the order promptly, communicate any delivery or pickup timing, and update the order status as it progresses. Timely communication reduces disputes and improves your seller rating.',
          'Taja Bazzar supports online payments through eSewa and Khalti where enabled, as well as cash on delivery (COD). Payment records appear in your Payments section. Review our payment guide for details on settlement and withdrawal requests for eligible sellers.',
        ],
      },
      {
        h: 'Tips for long-term success',
        p: [
          'Consistency matters more than volume. List what you actually have available each week rather than posting large quantities you cannot fulfill. Seasonal updates — such as highlighting monsoon greens or winter root vegetables — keep your storefront relevant.',
          'Encourage satisfied customers to leave reviews. High-rated farmers appear prominently in search results and on the Farmers directory. If you face a problem with an order, resolve it professionally through messages before it escalates to a report.',
        ],
      },
    ],
  },
  'farmer-verification': {
    slug: 'farmer-verification',
    path: '/farmer-verification',
    title: 'Farmer Verification Process',
    description:
      'Learn how Taja Bazzar verifies farmers to protect consumers and maintain trust in our local marketplace.',
    sections: [
      {
        h: 'Why we verify farmers',
        p: [
          'Verification helps consumers trust that sellers on Taja Bazzar are real local producers, not resellers passing off wholesale goods as farm-fresh. A verified badge on your profile signals that our team has reviewed your account and confirmed basic identity and location information.',
          'This process protects the entire community: honest farmers benefit from increased visibility, and consumers can shop with greater confidence knowing profiles have been checked.',
        ],
      },
      {
        h: 'What verification includes',
        p: [
          'Our verification review typically checks that your account uses accurate contact details, your farm location falls within our service districts (Morang, Sunsari, or Jhapa), and your profile represents a genuine agricultural seller. We may review product photos and listing patterns to detect misleading or duplicate content.',
          'Verification is not a certification of organic status or food safety compliance — those claims remain your responsibility and must be truthful. False organic or pesticide-free claims may result in account suspension.',
        ],
      },
      {
        h: 'How to get verified',
        p: [
          'Complete your farmer profile fully before requesting verification. Add a profile photo, cover image, farm bio, specialties, and at least one product listing with real photos. Incomplete profiles are not eligible for review.',
          'Ensure your map location pin reflects your actual farm or pickup point. Consumers use the Nearby feature to find local sellers; incorrect locations harm trust and delay verification.',
        ],
        list: [
          'Complete profile with photo, bio, and specialties',
          'Add at least one product with an original photo',
          'Set an accurate farm location on the map',
          'Use a reachable phone number and email',
          'Contact support if verification is delayed beyond 7 business days',
        ],
      },
      {
        h: 'After verification',
        p: [
          'Verified farmers display a checkmark badge on their public profile and in search results. You may appear in the "Verified Farmers" filter on the Farmers directory page.',
          'Maintain the standards that earned verification: keep listings accurate, respond to orders promptly, and update unavailable products. Verification can be revoked if we receive credible reports of fraud, misrepresentation, or repeated policy violations.',
        ],
      },
    ],
  },
  'farmer-support': {
    slug: 'farmer-support',
    path: '/farmer-support',
    title: 'Farmer Support & Help',
    description:
      'Get help with your Taja Bazzar farmer account — orders, payments, listings, verification, and technical issues.',
    sections: [
      {
        h: 'Support channels',
        p: [
          'Farmers can reach Taja Bazzar support by email at contact@tajabazzar.me or through the Contact page on our website. For order-specific questions, use the in-app messaging system to communicate directly with the consumer first — most issues resolve faster between buyer and seller.',
          'Our support team typically responds within one to two business days (Sunday–Friday, Nepal time). Include your registered email, farmer username, and order ID when reporting a problem so we can assist you quickly.',
        ],
      },
      {
        h: 'Common farmer questions',
        p: [
          'Listing issues: If a product is not appearing, check that quantity is greater than zero and that your account is in good standing. New listings may take a few minutes to appear in search results after saving.',
          'Order cancellations: If you cannot fulfill an order, update the status and message the customer immediately. Repeated cancellations without notice may affect your seller rating and visibility.',
          'Payment and withdrawals: Payment status for each order is visible in your Payments dashboard. For withdrawal requests or settlement questions, contact support with your transaction reference numbers.',
        ],
      },
      {
        h: 'Account and security',
        p: [
          'Keep your password secure and do not share login credentials. If you suspect unauthorized access, change your password immediately and contact support. Enable accurate recovery email so you can reset your password if needed.',
          'Report suspicious consumer behaviour (fake orders, harassment, or payment fraud) through the report feature or by emailing support with screenshots and order details.',
        ],
      },
      {
        h: 'Resources for sellers',
        p: [
          'Read our Seller Guide for step-by-step instructions on listing products and managing orders. Review the Farmer Verification page to understand how to earn a verified badge. Browse our Guides section for seasonal produce tips and payment tutorials relevant to eastern Nepal.',
        ],
        list: [
          'Seller Guide — full onboarding walkthrough',
          'Farmer Verification — how to earn verified status',
          'FAQ — answers for farmers, consumers, and payments',
          'Contact form — direct message to our team',
        ],
      },
    ],
  },
};

export function getFarmerResource(slug) {
  return FARMER_RESOURCES[slug] || null;
}

export const FARMER_RESOURCE_LIST = Object.values(FARMER_RESOURCES);
